/**
 * Shorts boards (F-140, docs/11; F-143): a board that holds ONE Short — shot
 * on one day, cut that week, released the week after. Until F-143
 * (2026-10-02) a Shorts board held a whole week of five; those boards keep
 * their five slots and keep working (the PATCH route, the rail, the cards
 * and the desktop read however many slots a board has), but every new one
 * is made with one.
 *
 * Everything here is PURE calendar arithmetic over `YYYY-MM-DD` keys (the
 * shape of `scriptFinishDate` and rainvic task deadlines): no Date parsing of
 * local time, no time zones, no locale. The client draws the plan the shoot
 * day implies, the server fills in whatever a caller left out, and both must
 * agree to the day — which is why the rules live in one place, tested, and
 * neither side re-derives them.
 *
 * THE ANCHOR IS THE SHOOT DAY. A Short hangs off one date, and the rest is a
 * plan derived from it (docs/11 §2):
 *
 *   scripts due   = the business day before the shoot
 *   edit due      = the Friday before the release
 *   release       = the Monday of the week AFTER the shoot's week
 *
 * A derived date is a DEFAULT, not a promise: every one of them is editable,
 * and the server stores what it was given. The derivation only answers
 * "what would this be if nobody said otherwise".
 */
/* ----------------------------- kinds + slots ---------------------------- */
/**
 * What a board is FOR. `long` is every board that existed before F-140 — one
 * long-form video, one script, one release — and the default everywhere a
 * kind is absent, so an older client's create and every existing row read
 * as long-form without a migration.
 */
export const BOARD_KINDS = ["long", "shorts"];
export function isBoardKind(value) {
    return BOARD_KINDS.includes(value);
}
/** F-143: one Short per board — the slot count every new Shorts board gets. */
export const SHORTS_PER_BOARD = 1;
/**
 * The most slots any board carries: the five of a Shorts WEEK made before
 * F-143. The wire schemas bound slot numbers by this, not by
 * `SHORTS_PER_BOARD`, so those boards can still be patched and imported into.
 */
export const MAX_SHORT_SLOTS = 5;
/**
 * Where a short is, beside the board's stage. On a one-Short board the two
 * mostly agree, but the stage ends at Editing while this goes on to
 * `released` — the release check-off, its task and its link. On a five-short
 * week from before F-143 it is also what tells Short 3 (released) from
 * Short 5 (still on the timeline).
 */
export const SHORT_STATES = ["planned", "shot", "cut", "released"];
export function isShortState(value) {
    return SHORT_STATES.includes(value);
}
/**
 * The slots after `patches` (by slot number; unknown slots are ignored, the
 * server refuses them before this runs). Pure and order-preserving; `nowIso`
 * is the release stamp, handed in because this layer reads no clock.
 */
export function applyShortSlotPatches(slots, patches, nowIso) {
    const bySlot = new Map(patches.map((patch) => [patch.slot, patch]));
    return slots.map((slot) => {
        const patch = bySlot.get(slot.slot);
        if (patch === undefined)
            return slot;
        const next = { ...slot };
        if (patch.title !== undefined)
            next.title = patch.title.trim();
        if (patch.releaseDate !== undefined)
            next.releaseDate = patch.releaseDate;
        if (patch.url !== undefined)
            next.url = patch.url === null ? null : patch.url.trim() || null;
        if (patch.state !== undefined && patch.state !== slot.state) {
            next.state = patch.state;
            next.releasedAt = patch.state === "released" ? nowIso : null;
        }
        return next;
    });
}
/**
 * The slots in the order they go out: by release day, then by slot number
 * for two on the same day. The SLOT NUMBER is a short's identity — it is
 * what the grid's `Short` column says on every one of its lines — so a
 * reorder never renumbers anything; it moves the DATES (`reorderReleases`),
 * and this is the order every rail and card draws.
 */
export function slotsInReleaseOrder(slots) {
    return [...slots].sort((a, b) => a.releaseDate.localeCompare(b.releaseDate) || a.slot - b.slot);
}
/**
 * The patches that put the shorts out in `slotOrder` (slot numbers, first
 * to release first) using the release days the week already has: the
 * earliest day goes to the first slot named, and so on. Dragging chip 3
 * ahead of chip 1 on the rail hands Short 3 the Monday and Short 1 the
 * Tuesday — nobody types a date, and the week keeps its five days (a
 * five-short week from before F-143; a one-Short board has nothing to
 * reorder). Slots
 * left out of `slotOrder` keep their dates; a repeated or unknown slot
 * yields no patches at all rather than a half-permutation.
 */
export function reorderReleases(slots, slotOrder) {
    const known = new Set(slots.map((slot) => slot.slot));
    const seen = new Set();
    for (const n of slotOrder) {
        if (!known.has(n) || seen.has(n))
            return [];
        seen.add(n);
    }
    const named = slots.filter((slot) => seen.has(slot.slot));
    const dates = named.map((slot) => slot.releaseDate).sort();
    const patches = [];
    slotOrder.forEach((n, index) => {
        const current = slots.find((slot) => slot.slot === n);
        const date = dates[index];
        if (current === undefined || date === undefined || current.releaseDate === date)
            return;
        patches.push({ slot: n, releaseDate: date });
    });
    return patches;
}
/**
 * Runtime guard for a slot list read back from the database (stored as
 * opaque JSON) or from an older client. A half-valid list reads as "no
 * slots" rather than rendering a card from `undefined` — same posture as the
 * design palette guard.
 */
export function isShortSlotArray(value) {
    if (!Array.isArray(value))
        return false;
    return value.every((entry) => typeof entry === "object" &&
        entry !== null &&
        Number.isInteger(entry.slot) &&
        typeof entry.title === "string" &&
        isCalendarDateKeyShape(entry.releaseDate) &&
        isShortState(entry.state) &&
        (entry.releasedAt === null || typeof entry.releasedAt === "string") &&
        (entry.url === null || typeof entry.url === "string"));
}
/* ------------------------------ the seed ------------------------------- */
/**
 * The column a five-short week (F-140 – F-142) was seeded with beside the
 * locked "Original Script": which short each line belongs to. Its contiguous
 * equal values are the RUNS such a board is organised around (docs/11 §4.2)
 * — one short, one run, the way the Mood column's runs work after "Divide
 * into sections". F-143: a one-Short board is seeded WITHOUT it — the whole
 * sheet is the short — but every reader of the column stays, for the weeks
 * that have one.
 */
export const SHORT_COLUMN_NAME = "Short";
export const SHORT_COLUMN_WIDTH = 72;
/**
 * Empty rows a short starts with. Four, not three: a short is a hook, two
 * beats and a close. (On a five-short week, a slot that started one row
 * short of that invited the first line of Short 2 into Short 1's run.)
 */
export const SHORT_ROWS_PER_SLOT = 4;
/**
 * The fill each slot's `Short` cells carry, slot 1 → 5 — what lines imported
 * into a short of a five-short week are painted with. Five of the F-27 pastel
 * presets (the same hexes the Mood column's topic fills use, so the two
 * columns read as one system), in an order where neighbours never share a
 * hue. Restated here rather than imported: the presets live in the client,
 * and the seed runs on the server.
 */
export const SHORT_SLOT_COLORS = [
    "#bfdbfe", // blue
    "#fed7aa", // orange
    "#bbf7d0", // green
    "#e9d5ff", // purple
    "#fef08a", // yellow
];
/* --------------------------- date-key arithmetic ------------------------ */
/** `YYYY-MM-DD` shape only — `isCalendarDateKey` (types.ts) checks the calendar. */
function isCalendarDateKeyShape(value) {
    return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
}
function parseKey(key) {
    const [y, m, d] = key.split("-").map(Number);
    return [y ?? NaN, m ?? NaN, d ?? NaN];
}
function toKey(utcMs) {
    const date = new Date(utcMs);
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, "0");
    const d = String(date.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}
/**
 * `key` shifted by `days` (negative = earlier). Done in UTC on purpose: a
 * calendar key has no zone, and local-time arithmetic would skip or repeat a
 * day across a DST change.
 */
export function addDateKeyDays(key, days) {
    const [y, m, d] = parseKey(key);
    return toKey(Date.UTC(y, m - 1, d + days));
}
/** Day of the week for a key: 0 = Sunday … 6 = Saturday. */
export function dateKeyWeekday(key) {
    const [y, m, d] = parseKey(key);
    return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}
/** The Monday of the week `key` falls in (a Sunday belongs to the week before). */
export function mondayOfWeek(key) {
    const weekday = dateKeyWeekday(key);
    const back = weekday === 0 ? 6 : weekday - 1;
    return addDateKeyDays(key, -back);
}
/** The last weekday (Mon–Fri) strictly before `key`. */
export function previousBusinessDay(key) {
    let cursor = addDateKeyDays(key, -1);
    while (dateKeyWeekday(cursor) === 0 || dateKeyWeekday(cursor) === 6) {
        cursor = addDateKeyDays(cursor, -1);
    }
    return cursor;
}
/**
 * The first Monday STRICTLY after `today` — the create dialog's default shoot
 * day. Strictly after, even on a Monday: a batch planned today is shot next
 * week, because its scripts are due the business day before the shoot and
 * that day has already passed.
 */
export function nextMondayKey(today) {
    const weekday = dateKeyWeekday(today);
    const ahead = weekday === 0 ? 1 : 8 - weekday;
    return addDateKeyDays(today, ahead);
}
export function shortsPlan(shootDate) {
    const releaseMonday = addDateKeyDays(mondayOfWeek(shootDate), 7);
    const releaseDates = [];
    for (let i = 0; i < SHORTS_PER_BOARD; i++)
        releaseDates.push(addDateKeyDays(releaseMonday, i));
    return {
        scriptFinishDate: previousBusinessDay(shootDate),
        editDueDate: addDateKeyDays(releaseMonday, -3),
        releaseDates,
    };
}
/**
 * The slots a new Shorts board starts with: untitled, planned, one per
 * release day in order (one, F-143). Overrides (a title, a moved release
 * day) are applied by slot number; anything else keeps the plan, and an
 * override for a slot the plan does not have — an older client still
 * sending a week of five — is dropped rather than inventing the slot.
 */
export function defaultShortSlots(releaseDates, overrides = []) {
    const byslot = new Map(overrides.map((entry) => [entry.slot, entry]));
    return releaseDates.map((releaseDate, index) => {
        const slot = index + 1;
        const override = byslot.get(slot);
        return {
            slot,
            title: override?.title?.trim() ?? "",
            releaseDate: override?.releaseDate ?? releaseDate,
            state: "planned",
            releasedAt: null,
            url: null,
        };
    });
}
/* ------------------------------- the name ------------------------------- */
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/**
 * The prefilled name of a Shorts board — "Short · Sep 21", after its shoot
 * day (F-143; a week of five was "Shorts · Week of Sep 21"). One pattern
 * everywhere so a short reads the same way in the kanban, the chat-room list
 * and the Projects page until someone names it after its topic. English
 * month abbreviations on purpose: this is a NAME that lands in the database,
 * not a rendering, and a name that changed with the creator's locale would
 * sort two ways.
 */
export function shortBoardName(shootDate) {
    const [, m, d] = parseKey(shootDate);
    return `Short · ${MONTHS[m - 1] ?? "?"} ${d}`;
}
//# sourceMappingURL=shorts.js.map